import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'preorder_auth_token';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // null = checking, false = logged out, string = role ('staff' | 'admin')
  const [status, setStatus] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    delete axios.defaults.headers.common['Authorization'];
    setStatus(false);
  }, []);

  // Attach token on mount + install a 401 interceptor
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setStatus(false);
      return;
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    axios
      .get(`${API}/auth/verify`)
      .then((res) => setStatus(res.data?.role || 'staff'))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        delete axios.defaults.headers.common['Authorization'];
        setStatus(false);
      });
  }, []);

  // Auto-logout on any 401 from the API, EXCEPT on /api/auth/login itself —
  // a wrong passcode there is a normal validation error, not a session failure
  // (matters for the in-app "Unlock Admin" modal: a wrong code shouldn't kick
  // an already-signed-in staff user back to the launcher).
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const url = err?.config?.url || '';
        const isLoginCall = url.endsWith('/api/auth/login');
        if (err?.response?.status === 401 && !isLoginCall) {
          localStorage.removeItem(TOKEN_KEY);
          delete axios.defaults.headers.common['Authorization'];
          setStatus(false);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const login = async (passcode) => {
    const { data } = await axios.post(`${API}/auth/login`, { passcode });
    localStorage.setItem(TOKEN_KEY, data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    setStatus(data.role || 'staff');
  };

  const role = typeof status === 'string' ? status : null;
  const isAuthed = !!role;
  const isAdmin = role === 'admin';

  return (
    <AuthContext.Provider value={{ status, role, isAuthed, isAdmin, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
